import React, { useCallback } from 'react';
import Modal from '#/common/components/Modal/Modal';
import TMMButton from '#/common/components/TMMButton';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';
import If from '../If';

export interface MatchItemsModalProps extends React.PropsWithChildren {
  visible: boolean;
  itemType: string;
  item: string;
  isSubmitting?: boolean;
  warningMessage?: string;
  currentMatchPreview?: React.ReactNode;
  onSecondaryAction: () => void;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
}

const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px !important;
`;

const ModalSubtitle = styled.div`
  margin-top: 4px;
  color: ${color('custom', 'constantGray')};
  font-size: 12px;
  font-weight: 400;
`;

const WarningMessage = styled.div`
  border: 1px solid ${color('warn', 'middle')};
  background-color: ${color('warn', 'middle', 0.05)};
  color: ${color('primary', 'dark')};
  font-family: 'Inter', Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  line-height: 1.2;
  border-radius: 16px;
  padding: 12px 24px;
  margin-top: 12px;
`;

const ModalContent = styled.div`
  .dx-scrollable-wrapper,
  .dx-datagrid-header-panel,
  .dx-toolbar-items-container,
  .dx-widget.dx-datagrid-pager.dx-pager {
    background-color: rgba(var(--custom-dataGridBackground-rgb), 1) !important;
  }
`;

const SectionLabel = styled.div`
  margin-top: 20px;
`;

const MatchItemsModal: React.FC<MatchItemsModalProps> = ({
  visible,
  itemType,
  item,
  children,
  isSubmitting,
  warningMessage = "All of the Asset's data will be updated after matching it with a new one.",
  currentMatchPreview,
  onSecondaryAction,
  onClose,
  onConfirm,
}) => {
  const handleConfirm = useCallback(async () => {
    await onConfirm();
  }, [onConfirm]);

  return (
    <Modal
      visible={visible}
      title={
        <ModalTitle>
          <div>Select Matching {itemType}</div>
          <ModalSubtitle>Selecting for {item}</ModalSubtitle>
        </ModalTitle>
      }
      dragEnabled
      width={1200}
      onHiding={onClose}
      content={
        <ModalContent>
          {currentMatchPreview}
          <If condition={!!currentMatchPreview}>
            <SectionLabel>Select New Matching</SectionLabel>
          </If>
          <WarningMessage>{warningMessage}</WarningMessage>
          {children}
        </ModalContent>
      }
      actions={[
        <TMMButton
          className="width-50"
          key="cancel-match-item"
          label={`Unmatch ${itemType}`}
          type="normal"
          onClick={onSecondaryAction}
        />,
        <TMMButton
          className="width-50"
          key="confirm-match-item"
          label={`Confirm ${itemType}`}
          type="submit"
          disabled={!!isSubmitting}
          onClick={handleConfirm}
        />,
      ]}
    />
  );
};

export default React.memo(MatchItemsModal);
